/**
 * Data hook behind the Discover Workspaces page: loads
 * `GET /workspaces/discover`, and flips a card's "Ask to join" /
 * "Cancel request" optimistically, rolling back and toasting on failure.
 * Filtering and sorting stay in the page (pure helpers in
 * `utils/discoverFilters.js`).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { sparkToast } from '@spark';
import { cancelJoinRequest, fetchDiscoverWorkspaces, requestToJoin } from '../api/discoverApi';

export const REQUEST_SENT_TITLE = 'Request sent';
export const REQUEST_SENT_BODY = "We've pinged the admins. You'll get an alert once you're in";
export const REQUEST_CANCELED_TITLE = 'Request canceled';
export const requestCanceledBody = (workspaceName) =>
    `Your request to join ${workspaceName || 'this workspace'} has been withdrawn.`;

const OPTIMISTIC_ID = 'pending';

/** 409 = the server knows better (already a member, already pending, nothing to cancel); 404 = gone. */
const needsRefresh = (status) => status === 404 || status === 409;

export default function useDiscoverWorkspaces() {
    const [workspaces, setWorkspaces] = useState([]);
    const [people, setPeople] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busyIds, setBusyIds] = useState(() => new Set());
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const load = useCallback(async ({ silent = false } = {}) => {
        if (!silent) {
            setLoading(true);
            setError('');
        }
        const result = await fetchDiscoverWorkspaces();
        if (!mounted.current) return;
        if (result.ok) {
            setWorkspaces(Array.isArray(result.data?.workspaces) ? result.data.workspaces : []);
            setPeople(result.data?.people && typeof result.data.people === 'object' ? result.data.people : {});
            setError('');
        } else if (!silent) {
            setError(result.error);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const setBusy = (workspaceId, busy) => {
        setBusyIds((prev) => {
            const next = new Set(prev);
            if (busy) next.add(workspaceId);
            else next.delete(workspaceId);
            return next;
        });
    };

    const patchWorkspace = useCallback((workspaceId, patch) => {
        setWorkspaces((prev) => prev.map((ws) => (ws.id === workspaceId ? { ...ws, ...patch } : ws)));
    }, []);

    const askToJoin = useCallback(async (workspace) => {
        if (!workspace || busyIds.has(workspace.id)) return false;
        setBusy(workspace.id, true);
        patchWorkspace(workspace.id, { join_request: { id: OPTIMISTIC_ID, status: 'pending' } });
        const result = await requestToJoin(workspace.id);
        if (!mounted.current) return false;
        if (result.ok) {
            patchWorkspace(workspace.id, { join_request: { id: result.data.id, status: result.data.status } });
            sparkToast.success(REQUEST_SENT_TITLE, { detailed: true, content: REQUEST_SENT_BODY });
        } else {
            patchWorkspace(workspace.id, { join_request: workspace.join_request || null });
            sparkToast.error('Could not send request', { detailed: true, content: result.error });
            if (needsRefresh(result.status)) load({ silent: true });
        }
        setBusy(workspace.id, false);
        return result.ok;
    }, [busyIds, load, patchWorkspace]);

    const cancelRequest = useCallback(async (workspace) => {
        if (!workspace || busyIds.has(workspace.id)) return false;
        setBusy(workspace.id, true);
        patchWorkspace(workspace.id, { join_request: null });
        const result = await cancelJoinRequest(workspace.id);
        if (!mounted.current) return false;
        if (result.ok) {
            sparkToast.success(REQUEST_CANCELED_TITLE, {
                detailed: true,
                content: requestCanceledBody(workspace.name),
            });
        } else {
            patchWorkspace(workspace.id, { join_request: workspace.join_request || null });
            sparkToast.error('Could not cancel request', { detailed: true, content: result.error });
            if (needsRefresh(result.status)) load({ silent: true });
        }
        setBusy(workspace.id, false);
        return result.ok;
    }, [busyIds, load, patchWorkspace]);

    return { workspaces, people, loading, error, busyIds, reload: load, askToJoin, cancelRequest };
}
